/**
 * AttachmentPreview
 *
 * Fetches a leave attachment via the authenticated axios client (Bearer token),
 * then renders it in a modal overlay.
 *  – PDF  → <iframe>
 *  – Image → <img>
 *  – Other → download-only
 */
import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Paperclip, X } from "lucide-react";
import { leaveApi } from "../api";

type PreviewState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "ready"; objectUrl: string; contentType: string; filename: string };

type Props = {
  /** Authenticated API URL, e.g. /api/hr/leave/attachments/22 */
  url: string | null | undefined;
  /** Optional display label shown on the trigger button */
  label?: string;
  /** Compact mode — just an icon button */
  compact?: boolean;
};

export function AttachmentPreview({ url, label = "View Document", compact = false }: Props) {
  const [state, setState] = useState<PreviewState>({ phase: "idle" });
  const objectUrlRef = useRef<string | null>(null);

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  if (!url) return null;

  async function open() {
    if (state.phase === "loading") return;
    setState({ phase: "loading" });
    try {
      // Fetch the raw blob — we create a blob URL for the <iframe>/<img>, so
      // Content-Disposition headers are irrelevant; ?inline=true is not needed
      // (and was causing a 500 on the backend).
      const { blob, contentType } = await leaveApi.downloadAttachment(url!);
      // Revoke any previous URL
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;

      const ext = guessExtension(contentType);
      const filename = `attachment${ext}`;
      setState({ phase: "ready", objectUrl, contentType, filename });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load attachment";
      setState({ phase: "error", message: msg });
    }
  }

  function close() {
    setState({ phase: "idle" });
  }

  function download() {
    if (state.phase !== "ready") return;
    const a = document.createElement("a");
    a.href = state.objectUrl;
    a.download = state.filename;
    a.click();
  }

  return (
    <>
      {/* ── Trigger button ── */}
      {compact ? (
        <button
          type="button"
          onClick={open}
          disabled={state.phase === "loading"}
          title={label}
          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
        >
          {state.phase === "loading"
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <Paperclip className="h-3 w-3" />}
          {state.phase === "loading" ? "Loading…" : "View"}
        </button>
      ) : (
        <button
          type="button"
          onClick={open}
          disabled={state.phase === "loading"}
          className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
        >
          {state.phase === "loading"
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Paperclip className="h-4 w-4" />}
          {state.phase === "loading" ? "Loading…" : label}
        </button>
      )}

      {/* ── Error banner (inline, not modal) ── */}
      {state.phase === "error" && (
        <p className="mt-1 text-xs font-semibold text-red-600">
          {state.message}{" "}
          <button type="button" onClick={() => setState({ phase: "idle" })} className="underline">
            Dismiss
          </button>
        </p>
      )}

      {/* ── Preview modal ── */}
      {state.phase === "ready" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            style={{ maxHeight: "90vh" }}>

            {/* Modal header */}
            <div className="flex shrink-0 items-center justify-between border-b bg-slate-50 px-5 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Paperclip className="h-4 w-4 text-slate-400" />
                Supporting Document
                <span className="text-xs font-normal text-slate-400">
                  ({state.contentType})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={download}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Preview area */}
            <div className="flex-1 overflow-auto bg-slate-100">
              {isPdf(state.contentType) ? (
                <iframe
                  src={state.objectUrl}
                  title="Document preview"
                  className="h-full min-h-[70vh] w-full border-0"
                />
              ) : isImage(state.contentType) ? (
                <div className="flex items-center justify-center p-6">
                  <img
                    src={state.objectUrl}
                    alt="Attachment"
                    className="max-h-[75vh] max-w-full rounded-xl object-contain shadow"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 py-16 text-slate-500">
                  <Paperclip className="h-12 w-12 text-slate-300" />
                  <p className="text-sm">Preview not available for this file type.</p>
                  <button
                    type="button"
                    onClick={download}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800"
                  >
                    <Download className="h-4 w-4" />
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function isPdf(ct: string) {
  return ct.includes("pdf");
}
function isImage(ct: string) {
  return ct.startsWith("image/");
}
function guessExtension(ct: string) {
  if (ct.includes("pdf"))       return ".pdf";
  if (ct.includes("png"))       return ".png";
  if (ct.includes("jpeg") || ct.includes("jpg")) return ".jpg";
  if (ct.includes("gif"))       return ".gif";
  if (ct.includes("webp"))      return ".webp";
  return "";
}
